import { useLocation } from "react-router-dom";

import { type ButtonProps } from "@braintrust/braintrust-ui-components";
import { useUser } from "@js/apps/common/hooks";
import { useUserNeedsIdentityVerification } from "@js/apps/identity-verification";
import { getAIInterviewUrl } from "@js/apps/interview/utils/ai-interview";
import {
  JobActionButtonBase,
  JobActionButtonPure,
} from "@js/apps/jobs/components/job-action-buttons/job-action-button-base";
import { useJobLocationContext } from "@js/apps/jobs/context";
import { useOpenMessengerModal } from "@js/apps/messenger/hooks/use-open-messenger-modal";
import { useSearchEventQueryId } from "@js/apps/tracking/search-event-slice";
import { openChoosePayoutMethodModal } from "@js/apps/withdrawal/components/choose-payout-method-modal/choose-payout-method-modal";
import { useAppDispatch } from "@js/hooks";
import { getURLWithBackLink } from "@js/utils";

import {
  clickBrowseOpenJobsButton,
  clickViewJobAfterSearch,
  viewJobClicked,
} from "../../actions";
import { getJobDetailsUrl } from "../../utils";

type ViewJobButtonProps = {
  jobId: number;
  fullWidth?: boolean;
  resultPosition?: number;
  searchResults?: boolean;
} & Omit<ButtonProps, "variant">;

export const ViewJobButton = ({
  jobId,
  searchResults,
  resultPosition,
  fullWidth,
  ...props
}: ViewJobButtonProps) => {
  const dispatch = useAppDispatch();
  const location = useJobLocationContext();
  const { search } = useLocation();
  const searchQueryId = useSearchEventQueryId(
    ENUMS.SearchEventOption.JOB_SEARCH_BOX,
  );

  const handleClick = () => {
    dispatch(viewJobClicked({ source: location, search }));

    if (searchResults && resultPosition !== undefined)
      dispatch(
        clickViewJobAfterSearch({
          jobId,
          location,
          resultPosition,
          searchQueryId,
        }),
      );
  };
  return (
    <JobActionButtonBase
      label="View job"
      variant="primary"
      withIcon
      fullWidth={fullWidth}
      onClick={handleClick}
      to={getJobDetailsUrl({ id: jobId, search, location })}
      {...props}
    />
  );
};

type ViewApplicationButtonProps = ButtonProps & {
  jobId: number;
  bidId?: number;
  withIcon?: boolean;
};

export const ViewApplicationButton = ({
  jobId,
  bidId,
  ...props
}: ViewApplicationButtonProps) => {
  if (!bidId) return null;

  return (
    <JobActionButtonBase
      label="View application"
      to={getURLWithBackLink(`/jobs/${jobId}/proposals/${bidId}/edit/`)}
      {...props}
    />
  );
};

type ScheduleInterviewButtonProps = ButtonProps & {
  employerUserId?: number;
  withIcon?: boolean;
};

export const ScheduleInterviewButton = ({
  employerUserId,
  ...props
}: ScheduleInterviewButtonProps) => {
  const user = useUser();
  const { openMessengerModal } = useOpenMessengerModal();

  if (!user || !employerUserId) return null;

  const handleScheduleInterview = () => {
    void openMessengerModal({
      context: {
        participants: [employerUserId, user.id],
      },
    });
  };

  return (
    <JobActionButtonBase
      label="Schedule interview"
      onClick={handleScheduleInterview}
      {...props}
    />
  );
};

export type OfferButtonProps = Omit<ButtonProps, "children"> & {
  jobId: number;
  offerId: number;
};

export const ReviewContractButton = ({
  jobId,
  offerId,
  ...props
}: OfferButtonProps) => {
  return (
    <JobActionButtonBase
      label="Review contract"
      to={getURLWithBackLink(`/jobs/${jobId}/offers/${offerId}`)}
      {...props}
    />
  );
};

export const RespondToOfferButton = ({
  jobId,
  offerId,
  ...props
}: OfferButtonProps) => {
  const { userFailedIdentityVerification } = useUserNeedsIdentityVerification();
  if (userFailedIdentityVerification) {
    return <></>;
  }

  return (
    <JobActionButtonBase
      label="Respond to offer"
      to={getURLWithBackLink(`/jobs/${jobId}/offers/${offerId}`)}
      {...props}
    />
  );
};

export const BrowseOpenJobs = (props: Omit<ButtonProps, "variant">) => {
  const dispatch = useAppDispatch();
  const handleClick = () => {
    dispatch(clickBrowseOpenJobsButton());
  };

  return (
    <JobActionButtonBase
      label="Browse open jobs"
      to="/jobs"
      variant="primary"
      onClick={handleClick}
      {...props}
    />
  );
};

type StartAIInterviewButtonProps = ButtonProps & {
  bidId: number;
  withIcon?: boolean;
};

export const StartAIInterviewButton = ({
  bidId,
  ...props
}: StartAIInterviewButtonProps) => {
  return (
    <JobActionButtonBase
      label="Start AI interview"
      to={getAIInterviewUrl({ bidId })}
      {...props}
    />
  );
};

type PendingBankingInfoButtonProps = ButtonProps;

export const PendingBankingInfoButton = (
  props: PendingBankingInfoButtonProps,
) => {
  const handleClick = () => {
    openChoosePayoutMethodModal();
  };

  return (
    <JobActionButtonPure label="Add Banking" onClick={handleClick} {...props} />
  );
};
